/**
 * Registry of routing canaries — decisions about which backend serves a route, made in the
 * middleware before render. Unlike {@link AMPLITUDE_EXPERIMENTS}, which toggle UI on the client.
 *
 * Adding a canary is one {@link defineCanary} entry in {@link CANARIES}; the header, cookie,
 * middleware guard and exposure event are all derived from it.
 *
 * No SDK imports here, so the middleware, SSR readers and client components can all share it.
 */

import { isNativeApiAllowedRoute } from '@/logic/app-data/app-data-route';

export type Canary<TVariant extends string = string> = {
    /** Stable slug behind the `x-canary-<id>` header and `canary_<id>` cookie. */
    id: string;
    /** Amplitude flag key, declared in `@/utils/experiments`. */
    flagKey: string;
    /** MUST be the existing, proven path: every failure mode resolves here. */
    control: TVariant;
    /** Variants the routing code can serve. Anything else degrades to `control`. */
    variants: readonly TVariant[];
    /** Paths this canary decides. Keep narrow — it gates an Amplitude evaluation. */
    matches: (path: string) => boolean;
};

/** Preserves the literal union of `variants` so consumers get a typed variant, not `string`. */
export const defineCanary = <const TVariant extends string>(canary: Canary<TVariant>): Canary<TVariant> => canary;

export type ContentApiVariant = 'page-config' | 'native-api';

export const NATIVE_API_FLAG_KEY = 'casino-next-content-api';

export const NATIVE_API_CANARY = defineCanary<ContentApiVariant>({
    id: 'native-api',
    flagKey: NATIVE_API_FLAG_KEY,
    control: 'page-config',
    variants: ['page-config', 'native-api'],
    matches: isNativeApiAllowedRoute
});

export type GfeVariant = 'aggregation' | 'gfe';

export const GFE_FLAG_KEY = 'casino-gaming-fusion-engine-rollout';

/**
 * GFE serves category pages only. Hub, influencer-hub and gameplay-session routes are
 * category-backed too — `normalizeRouteForPageConfig` maps each of them onto a `/c/<slug>` GFE
 * lookup — so they are in scope.
 *
 * The prefixes are literals rather than `ROUTES` imports on purpose: this module is loaded by the
 * middleware and by the client `CanaryExposure`, so it must not pull in heavier graphs.
 * `app-data-route.ts` inlines the same prefixes for the same reason.
 */
const GFE_CATEGORY_ROUTE_PREFIXES = ['/c/', '/influencer-hub/', '/gameplay-session/'];

const isGfeCategoryRoute = (path: string): boolean => {
    const route = path.split('?')[0] || '/';

    return GFE_CATEGORY_ROUTE_PREFIXES.some((prefix) => route.startsWith(prefix) && route.length > prefix.length);
};

/**
 * Rolls category pages onto the Gaming Fusion Engine. Independent of {@link NATIVE_API_CANARY}, so
 * a visitor can be assigned `gfe` on this flag and `native-api` on that one; GFE takes precedence,
 * and the native API is only consulted once GFE misses.
 */
export const GFE_CANARY = defineCanary<GfeVariant>({
    id: 'gfe',
    flagKey: GFE_FLAG_KEY,
    control: 'aggregation',
    variants: ['aggregation', 'gfe'],
    matches: isGfeCategoryRoute
});

/**
 * Canaries cover logged-in visitors only — logged-out responses are CDN-cached, so one person's
 * assignment would be served to everyone behind the same cache entry. Size rollouts accordingly.
 */
export const CANARIES: readonly Canary[] = [NATIVE_API_CANARY, GFE_CANARY];

export const canaryHeaderName = (canary: Canary): string => `x-canary-${canary.id}`;

export const canaryCookieName = (canary: Canary): string => `canary_${canary.id}`;

export const getCanariesForPath = (path: string): Canary[] => CANARIES.filter((canary) => canary.matches(path));

/** Narrows an untrusted string (header, cookie) to a variant this canary declares. */
export const toVariant = <TVariant extends string>(canary: Canary<TVariant>, value: string | null | undefined) =>
    canary.variants.find((variant) => variant === value);
